import { FunctionComponent, memo, useCallback, useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import isEqual from 'fast-deep-equal'

import useHasJWPlayerScriptLoaded from '@hooks/useHasJWPlayerScriptLoaded'

import { JWPlayer } from '../types'
import { logVideoPlayer } from '../utils/logger'
import { ExtendedWindow, ReactJWPlayerProps } from './types'

type LibPropType = ReactJWPlayerProps & {
  widgetId?: string
  onSetup?: (player: JWPlayer) => void
}

const ReactJWPlayer: FunctionComponent<LibPropType> = (props) => {
  const queryClient = useQueryClient()
  const hasPlayerInitialized = useRef(false)
  const hasJWPlayerScriptLoaded = useHasJWPlayerScriptLoaded()
  const containerRef = useRef<HTMLDivElement>(null)

  const initialize = useCallback(() => {
    if (containerRef.current) {
      const player = (window as ExtendedWindow)?.jwplayer(
        containerRef.current,
      ) as JWPlayer

      if (player) {
        const { onSetup, ...playerProps } = props
        const playerSetupOptions = {
          ...playerProps,
          key: queryClient.getQueryData<string>(['jwplayer-key']) as string,
        }

        logVideoPlayer(
          { queryClient },
          'initialize::',
          player,
          playerSetupOptions,
        )

        onSetup?.(player)

        player.setup(playerSetupOptions)
      }
    }
  }, [queryClient, props])

  useEffect(() => {
    if (!hasPlayerInitialized.current && hasJWPlayerScriptLoaded) {
      logVideoPlayer(
        { queryClient },
        'isJWPlayerScriptLoaded::shouldInitialize',
      )
      hasPlayerInitialized.current = true
      initialize()
    }
  }, [hasJWPlayerScriptLoaded, hasPlayerInitialized, initialize, queryClient])

  return (
    <div className={props.className} data-chromatic="ignore">
      <div id={props.widgetId} ref={containerRef} />
    </div>
  )
}

/** do not re-render the component unless the playlist changes */
const propsAreEqual = (
  prevProps: ReactJWPlayerProps,
  nextProps: ReactJWPlayerProps,
): boolean => isEqual(prevProps.playlist, nextProps.playlist)

export default memo(ReactJWPlayer, propsAreEqual)
